import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useCreateBlockNote } from '@blocknote/react';
import { BlockNoteView } from '@blocknote/mantine';
import { en } from '@blocknote/core/locales';
import "@blocknote/core/fonts/inter.css";
import "@blocknote/mantine/style.css";
import { getNotionNotes, getNotionNoteContent, createNotionNote, updateNotionNote, deleteNotionNote, publishNote, unpublishNote, uploadImage } from '../api';
import {
  Plus,
  ArrowLeft,
  Trash2,
  Search,
  MoreVertical,
  FileText,
  X,
  ExternalLink,
  Globe,
  Copy,
  Check,
} from 'lucide-react';
import { Badge } from './catalyst/badge';
import { Button } from './catalyst/button';
import { Dialog, DialogActions, DialogDescription, DialogTitle } from './catalyst/dialog';
import { Input } from './catalyst/input';

const STORAGE_KEY = 'calendar-app-notes';

const NOTE_EMOJIS = [
  '\u{1F4DD}', '\u{1F4A1}', '\u{2B50}', '\u{1F680}', '\u{1F3AF}', '\u{1F4DA}', '\u{1F4CB}', '\u{270F}\uFE0F',
  '\u{1F4CC}', '\u{1F516}', '\u{1F5C2}\uFE0F', '\u{1F4D6}', '\u{1F4AC}', '\u{1F50D}', '\u{2705}', '\u{1F3C6}',
  '\u{1F30D}', '\u{1F525}', '\u{1F4A6}', '\u{1F308}', '\u{1F33F}', '\u{2600}\uFE0F', '\u{1F319}', '\u{26A1}',
  '\u{1F381}', '\u{1F3B5}', '\u{1F4F7}', '\u{2764}\uFE0F', '\u{1F60A}', '\u{1F914}',
];

function randomEmoji() {
  return NOTE_EMOJIS[Math.floor(Math.random() * NOTE_EMOJIS.length)];
}

function loadNotes() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : [];
  } catch {
    return [];
  }
}

function saveNotes(notes) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(notes));
}



function NoteEditor({ note, onSave, onBack, onDelete, onPublishChange, isMobile }) {
  const [title, setTitle] = useState(note?.title || '');
  const [emoji, setEmoji] = useState(note?.emoji || randomEmoji());
  const [showMenu, setShowMenu] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [copied, setCopied] = useState(false);
  const titleInputRef = useRef(null);

  const isNewNote = !note?.fromNotion || note?.id?.startsWith('draft-');
  const contentLoadedRef = useRef(false);
  const titleRef = useRef(title);
  const emojiRef = useRef(emoji);
  const isFirstRender = useRef(true);
  const editorRef = useRef(null);

  useEffect(() => { titleRef.current = title; }, [title]);
  useEffect(() => { emojiRef.current = emoji; }, [emoji]);

  const editor = useCreateBlockNote({
    uploadFile: async (file) => {
      return await uploadImage(file);
    },
    dictionary: {
      ...en,
      slash_menu: {
        ...en.slash_menu,
        check_list: {
          ...en.slash_menu.check_list,
          title: "To-do List",
          aliases: ["todo", "to-do", "checklist", "check list", "checkbox"],
        },
      },
    },
  });

  // Store editor ref for access in callbacks
  useEffect(() => {
    editorRef.current = editor;
  }, [editor]);

  // Load content into BlockNote editor — prefer JSON blocks (lossless) over HTML
  useEffect(() => {
    if (!editor || contentLoadedRef.current) return;
    const hasBlocks = note?.blocks && Array.isArray(note.blocks) && note.blocks.length > 0;
    const hasContent = note?.content;

    if (hasBlocks) {
      try {
        editor.replaceBlocks(editor.document, note.blocks);
      } catch (e) {
        console.error('Failed to load blocks:', e);
      }
      contentLoadedRef.current = true;
    } else if (hasContent) {
      try {
        const blocks = editor.tryParseHTMLToBlocks(note.content);
        editor.replaceBlocks(editor.document, blocks);
      } catch (e) {
        console.error('Failed to parse HTML to blocks:', e);
      }
      contentLoadedRef.current = true;
    } else if (isNewNote) {
      // New empty note — mark loaded so edits save immediately
      contentLoadedRef.current = true;
    }
    // Otherwise: content is still being fetched, don't mark loaded yet
  }, [editor, note?.content, note?.blocks, isNewNote]);

  // Auto-focus title input for new notes
  const titleRefCallback = useCallback((node) => {
    titleInputRef.current = node;
    if (node && isNewNote && !title) {
      node.focus();
      const proxy = document.getElementById('ios-keyboard-proxy');
      if (proxy) proxy.remove();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getEditorContent = useCallback(() => {
    if (!editorRef.current) return { html: '', blocks: [] };
    try {
      const blocks = editorRef.current.document;
      const html = editorRef.current.blocksToHTMLLossy(blocks);
      return { html, blocks };
    } catch {
      return { html: '', blocks: [] };
    }
  }, []);

  const handleEditorChange = useCallback(() => {
    if (!contentLoadedRef.current) return;
    const { html, blocks } = getEditorContent();
    onSave({
      ...note,
      title: titleRef.current || 'Untitled',
      emoji: emojiRef.current,
      content: html,
      blocks,
      updatedAt: new Date().toISOString(),
    });
  }, [note, onSave, getEditorContent]);

  useEffect(() => {
    if (!editor || isFirstRender.current || !contentLoadedRef.current) return;
    const timeout = setTimeout(() => {
      const { html, blocks } = getEditorContent();
      onSave({
        ...note,
        title: title || 'Untitled',
        emoji: emojiRef.current,
        content: html,
        blocks,
        updatedAt: new Date().toISOString(),
      });
    }, 300);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (!editor || !contentLoadedRef.current) return;
    (() => {
      const { html, blocks } = getEditorContent();
      onSave({
        ...note,
        title: titleRef.current || 'Untitled',
        emoji,
        content: html,
        blocks,
        updatedAt: new Date().toISOString(),
      });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emoji]);

  const handleTitleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      editor?.focus();
    }
  };

  const publishedSlug = note?.publishedSlug;
  const publishedUrl = publishedSlug ? `${window.location.origin}/p/${publishedSlug}` : null;

  const handlePublish = async () => {
    if (publishing) return;
    setPublishing(true);
    try {
      const notionId = note.notionId || note.id;
      const { slug } = await publishNote(notionId);
      onPublishChange?.(note.id, slug);
    } catch (e) {
      console.error('Failed to publish:', e);
    } finally {
      setPublishing(false);
    }
  };

  const handleUnpublish = async () => {
    if (publishing) return;
    setPublishing(true);
    try {
      const notionId = note.notionId || note.id;
      await unpublishNote(notionId);
      onPublishChange?.(note.id, null);
    } catch (e) {
      console.error('Failed to unpublish:', e);
    } finally {
      setPublishing(false);
    }
  };

  const handleCopyLink = () => {
    if (!publishedUrl) return;
    navigator.clipboard.writeText(publishedUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Publish menu items for the dropdown (mobile only)
  const publishMenuItems = (
    <>
      {publishedSlug ? (
        <>
          <button type="button" onClick={() => { handleCopyLink(); setShowMenu(false); }} className="notion-dropdown-item">
            {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />} {copied ? 'Copied!' : 'Copy link'}
          </button>
          <button type="button" onClick={() => { handleUnpublish(); setShowMenu(false); }} className="notion-dropdown-item text-orange-500">
            <Globe className="w-4 h-4" /> Unpublish
          </button>
        </>
      ) : (
        <button type="button" onClick={() => { handlePublish(); setShowMenu(false); }} className="notion-dropdown-item" disabled={publishing}>
          <Globe className="w-4 h-4" /> {publishing ? 'Publishing...' : 'Publish'}
        </button>
      )}
    </>
  );

  // --- Header actions shared by mobile/desktop ---
  const headerActions = (
    <div className="flex items-center gap-1">
      {/* Desktop: inline publish button + link */}
      {!isMobile && (
        publishedSlug ? (
          <div className="flex items-center gap-1 mr-1">
            <Button
              type="button"
              outline
              onClick={handleCopyLink}
              className="px-2.5 py-1 text-xs"
              title={publishedUrl}
            >
              <Globe className="h-3.5 w-3.5" />
              <span className="max-w-[100px] truncate">{copied ? 'Copied!' : `/p/${publishedSlug}`}</span>
              {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            </Button>
            <Button
              type="button"
              plain
              onClick={handleUnpublish}
              className="h-8 w-8 rounded-full p-0 text-orange-500 hover:bg-orange-50"
              title="Unpublish"
              disabled={publishing}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        ) : (
          <Button
            type="button"
            outline
            onClick={handlePublish}
            className="mr-1 px-2.5 py-1.5 text-xs"
            disabled={publishing}
            title="Publish note"
          >
            <Globe className="w-3.5 h-3.5" />
            {publishing ? 'Publishing...' : 'Publish'}
          </Button>
        )
      )}
      <div className="relative">
        <Button type="button" plain onClick={() => setShowMenu(!showMenu)} className="h-8 w-8 rounded-full p-0" aria-label="Note menu">
          <MoreVertical className="h-4 w-4" />
        </Button>
        {showMenu && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setShowMenu(false)} />
            <div className="notion-dropdown">
              {/* Only show publish items in dropdown on mobile */}
              {isMobile && publishMenuItems}
              {note.notionUrl && (
                <a
                  href={note.notionUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setShowMenu(false)}
                  className="notion-dropdown-item"
                >
                  <ExternalLink className="w-4 h-4" /> Open in Notion
                </a>
              )}
              <button
                type="button"
                onClick={() => { setShowMenu(false); onDelete(note.id); }}
                className="notion-dropdown-item text-red-500"
              >
                <Trash2 className="w-4 h-4" /> Delete
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );

  // --- Emoji + Title area (Notion style) ---
  const titleArea = (
    <div className={`notion-title-area ${isMobile ? 'px-6 pt-6 pb-1' : 'px-[54px] pt-10 pb-1'}`}>
      <div className="flex items-center gap-3">
        <div className="relative flex-shrink-0">
          <button
            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
            className="notion-emoji-btn"
          >
            {emoji}
          </button>
          {showEmojiPicker && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setShowEmojiPicker(false)} />
              <div className="notion-emoji-picker">
                {NOTE_EMOJIS.map(e => (
                  <button
                    key={e}
                    onClick={() => { setEmoji(e); setShowEmojiPicker(false); }}
                    className="notion-emoji-option"
                  >
                    {e}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
        <Input
          ref={titleRefCallback}
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={handleTitleKeyDown}
          placeholder="Untitled"
          className="notion-title-input"
        />
      </div>
    </div>
  );

  const editorView = (
    <div className="blocknote-wrapper" data-mobile={isMobile ? "" : undefined}>
      <BlockNoteView
        editor={editor}
        theme="light"
        onChange={handleEditorChange}
        sideMenu={!isMobile}
        emojiPicker={true}
      />
    </div>
  );

  if (isMobile) {
    return (
      <>
        <div className="flex flex-shrink-0 items-center justify-between border-b border-zinc-200 bg-white px-3 py-2">
          <Button type="button" plain onClick={onBack} className="h-9 w-9 rounded-full p-0" aria-label="Back to notes">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-1">
            <Button type="button" plain onClick={onBack} className="h-9 w-9 rounded-full p-0" title="Close">
              <X className="h-5 w-5" />
            </Button>
            <div className="relative">
              <Button type="button" plain onClick={() => setShowMenu(!showMenu)} className="h-9 w-9 rounded-full p-0" aria-label="Note menu">
                <MoreVertical className="h-4 w-4" />
              </Button>
              {showMenu && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowMenu(false)} />
                  <div className="notion-dropdown">
                    {publishMenuItems}
                    {note.notionUrl && (
                      <a
                        href={note.notionUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setShowMenu(false)}
                        className="notion-dropdown-item"
                      >
                        <ExternalLink className="w-4 h-4" /> Open in Notion
                      </a>
                    )}
                    <button
                      type="button"
                      onClick={() => { setShowMenu(false); onDelete(note.id); }}
                      className="notion-dropdown-item text-red-500"
                    >
                      <Trash2 className="w-4 h-4" /> Delete
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto bg-white">
          {titleArea}
          {editorView}
        </div>
      </>
    );
  }

  // Desktop editor — Notion layout
  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-white">
      <div className="flex flex-shrink-0 items-center justify-between border-b border-zinc-200 bg-white px-3 py-2">
        <Button type="button" plain onClick={onBack} className="h-8 w-8 rounded-full p-0" aria-label="Back to notes">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        {headerActions}
      </div>
      <div className="flex-1 overflow-y-auto notion-page-body">
        {titleArea}
        {editorView}
      </div>
    </div>
  );
}

export function useNotesState(skip) {
  const [notes, setNotes] = useState(loadNotes);
  const [notionLoading, setNotionLoading] = useState(true);

  useEffect(() => {
    if (skip) return;
    let cancelled = false;
    (async () => {
      try {
        const notionNotes = await getNotionNotes();
        if (cancelled) return;
        let merged;
        setNotes(prev => {
          const notionIds = new Set(notionNotes.map(n => n.id));
          const drafts = prev.filter(n =>
            n.id.startsWith('draft-') && (!n.notionId || !notionIds.has(n.notionId))
          );
          merged = [...notionNotes.map(n => ({ ...n, fromNotion: true })), ...drafts];
          saveNotes(merged);
          return merged;
        });

        // Preload content for the top 10 most recent notes
        if (merged) {
          const top10 = merged
            .filter(n => n.fromNotion && !n.content)
            .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
            .slice(0, 10);

          const results = await Promise.all(
            top10.map(n => getNotionNoteContent(n.id).catch(() => null))
          );

          if (cancelled) return;
          setNotes(prev => {
            let updated = prev;
            for (let i = 0; i < top10.length; i++) {
              if (results[i]?.content || results[i]?.blocks) {
                updated = updated.map(n =>
                  n.id === top10[i].id ? { ...n, content: results[i].content, blocks: results[i].blocks } : n
                );
              }
            }
            saveNotes(updated);
            return updated;
          });
        }
      } catch (e) {
        console.error('Failed to fetch Notion notes:', e);
      } finally {
        if (!cancelled) setNotionLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [skip]);

  return { notes, setNotes, notionLoading };
}

export default function Notes({ isMobile, notesState, onNoteNavigate, initialNoteId }) {
  const localState = useNotesState(!!notesState);
  const { notes, setNotes } = notesState || localState;

  const [activeNoteId, setActiveNoteId] = useState(initialNoteId || null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSheet, setShowSheet] = useState(!!initialNoteId && isMobile);
  const [sheetAnimating, setSheetAnimating] = useState(!!initialNoteId && isMobile);
  const sheetRef = useRef(null);
  const backdropRef = useRef(null);
  const swipeRef = useRef({ startX: 0, startY: 0, tracking: false, started: false });

  // Open the initial note if we have one (deep link)
  const initialOpenDone = useRef(false);
  useEffect(() => {
    if (initialNoteId && notes.length > 0 && !initialOpenDone.current) {
      initialOpenDone.current = true;
      openNote(initialNoteId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialNoteId, notes.length]);

  // Listen for browser back/forward
  useEffect(() => {
    const handleRouteChange = (e) => {
      const { noteId, workspace } = e.detail;
      if (workspace === 'notes') {
        if (noteId) {
          setActiveNoteId(noteId);
          if (isMobile) setShowSheet(true);
        } else {
          setActiveNoteId(null);
          setShowSheet(false);
        }
      }
    };
    window.addEventListener('route-change', handleRouteChange);
    return () => window.removeEventListener('route-change', handleRouteChange);
  }, [isMobile]);

  const activeNote = notes.find(n => n.id === activeNoteId);

  const createNote = () => {
    // On iOS, programmatic focus() only works within a user gesture.
    // Create and focus a temporary input synchronously during the tap,
    // so the keyboard opens immediately. The real title input will steal
    // focus when it mounts via callback ref, then remove this proxy.
    const proxy = document.createElement('input');
    proxy.id = 'ios-keyboard-proxy';
    proxy.style.cssText = 'position:fixed;top:-100px;opacity:0;height:0;';
    document.body.appendChild(proxy);
    proxy.focus();

    const existingDraft = notes.find(n => n.pendingNotion && !n.title && !n.content);
    if (existingDraft) {
      setActiveNoteId(existingDraft.id);
      if (isMobile) setShowSheet(true);
      onNoteNavigate?.(existingDraft.id);
      // Clean up proxy after a tick — the editor is already mounted so callback ref won't re-fire
      setTimeout(() => proxy.remove(), 300);
      return;
    }

    const emoji = randomEmoji();
    const localNote = {
      id: 'draft-' + Date.now(),
      title: '',
      content: '',
      emoji,
      pendingNotion: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    setNotes(prev => {
      const updated = [localNote, ...prev];
      saveNotes(updated);
      return updated;
    });
    setActiveNoteId(localNote.id);
    onNoteNavigate?.(localNote.id);
    if (isMobile) { setShowSheet(true); requestAnimationFrame(() => setSheetAnimating(true)); }
  };

  const openNote = async (id) => {
    setActiveNoteId(id);
    if (isMobile) { setShowSheet(true); requestAnimationFrame(() => setSheetAnimating(true)); }
    onNoteNavigate?.(id);

    const note = notes.find(n => n.id === id);
    if (note?.fromNotion && !note.content && !note.blocks) {
      try {
        const data = await getNotionNoteContent(id);
        setNotes(prev => {
          const updated = prev.map(n => n.id === id ? { ...n, content: data.content, blocks: data.blocks } : n);
          saveNotes(updated);
          return updated;
        });
      } catch (e) {
        console.error('Failed to fetch note content:', e);
      }
    }
  };

  const closeSheet = () => {
    setSheetAnimating(false);
    setTimeout(() => {
      setShowSheet(false);
      setActiveNoteId(null);
      onNoteNavigate?.(null);
    }, 300);
  };

  const handleSheetTouchStart = (e) => {
    const touch = e.touches[0];
    swipeRef.current = { startX: touch.clientX, startY: touch.clientY, tracking: true, started: false };
  };

  const handleSheetTouchMove = (e) => {
    const sw = swipeRef.current;
    if (!sw.tracking) return;
    const touch = e.touches[0];
    const dx = touch.clientX - sw.startX;
    const dy = Math.abs(touch.clientY - sw.startY);

    // If vertical scroll dominates, stop tracking
    if (!sw.started && dy > 10 && dy > Math.abs(dx)) {
      sw.tracking = false;
      return;
    }

    // Only start swipe for rightward movement
    if (!sw.started) {
      if (dx < 10) return;
      sw.started = true;
      // Disable CSS transition during gesture
      if (sheetRef.current) sheetRef.current.style.transition = 'none';
    }

    const offset = Math.max(0, dx);
    const width = window.innerWidth;
    const progress = offset / width;
    if (sheetRef.current) {
      sheetRef.current.style.transform = `translateX(${offset}px)`;
      sheetRef.current.style.boxShadow = `-12px 0 40px rgba(0, 0, 0, ${0.15 * (1 - progress)})`;
    }
  };

  const handleSheetTouchEnd = (e) => {
    const sw = swipeRef.current;
    if (!sw.tracking || !sw.started) {
      sw.tracking = false;
      return;
    }
    sw.tracking = false;

    const touch = e.changedTouches[0];
    const dx = touch.clientX - sw.startX;
    const width = window.innerWidth;

    // Re-enable transitions
    const transition = 'transform 0.3s ease-out, box-shadow 0.3s ease-out';
    if (sheetRef.current) sheetRef.current.style.transition = transition;

    if (dx > width * 0.3) {
      // Dismiss
      if (sheetRef.current) {
        sheetRef.current.style.transform = 'translateX(100%)';
        sheetRef.current.style.boxShadow = 'none';
      }
      setTimeout(() => {
        setShowSheet(false);
        setSheetAnimating(false);
        setActiveNoteId(null);
        onNoteNavigate?.(null);
      }, 300);
    } else {
      // Snap back
      if (sheetRef.current) {
        sheetRef.current.style.transform = 'translateX(0)';
        sheetRef.current.style.boxShadow = '-12px 0 40px rgba(0, 0, 0, 0.15)';
      }
    }
  };

  const createTimerRef = useRef(null);
  const updateTimerRef = useRef(null);
  const creatingRef = useRef(false);

  const handleSave = useCallback((updatedNote) => {
    setNotes(prev => {
      const existing = prev.find(n => n.id === updatedNote.id);
      const merged = {
        ...existing,
        ...updatedNote,
        fromNotion: updatedNote.fromNotion || existing?.fromNotion,
        pendingNotion: existing?.pendingNotion && !updatedNote.fromNotion,
      };
      const updated = prev.map(n => n.id === updatedNote.id ? merged : n);
      saveNotes(updated);

      if (merged.pendingNotion && !creatingRef.current) {
        const hasContent = (merged.title && merged.title !== 'Untitled') || merged.content;
        if (hasContent) {
          creatingRef.current = true;
          if (createTimerRef.current) clearTimeout(createTimerRef.current);
          createTimerRef.current = setTimeout(async () => {
            try {
              const created = await createNotionNote({
                title: merged.title,
                emoji: merged.emoji,
                content: merged.content,
                blocks: merged.blocks,
              });
              setNotes(p => {
                const swapped = p.map(n =>
                  n.id === merged.id ? { ...n, notionId: created.id, notionUrl: created.notionUrl, fromNotion: true, pendingNotion: false } : n
                );
                saveNotes(swapped);
                creatingRef.current = false;
                return swapped;
              });
            } catch (e) {
              console.error('Failed to create note in Notion:', e);
              creatingRef.current = false;
            }
          }, 300);
        }
      } else if (merged.fromNotion && !merged.pendingNotion) {
        const notionId = merged.notionId || merged.id;
        if (updateTimerRef.current) clearTimeout(updateTimerRef.current);
        updateTimerRef.current = setTimeout(() => {
          updateNotionNote({
            id: notionId,
            title: merged.title,
            emoji: merged.emoji,
            content: merged.content,
            blocks: merged.blocks,
          }).catch(e => console.error('Failed to save to Notion:', e));
        }, 500);
      }

      return updated;
    });
  }, [setNotes]);

  const handlePublishChange = useCallback((noteId, slug) => {
    setNotes(prev => {
      const updated = prev.map(n => n.id === noteId ? { ...n, publishedSlug: slug } : n);
      saveNotes(updated);
      return updated;
    });
  }, [setNotes]);

  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [showPublished, setShowPublished] = useState(false);

  const publishedCount = notes.filter(n => n.publishedSlug).length;

  const confirmDelete = (id) => {
    setDeleteConfirmId(id);
  };

  const handleDelete = async (id) => {
    setDeleteConfirmId(null);
    const noteToDelete = notes.find(n => n.id === id);
    const updated = notes.filter(n => n.id !== id);
    setNotes(updated);
    saveNotes(updated);
    setActiveNoteId(null);
    if (isMobile) setShowSheet(false);

    const notionId = noteToDelete?.notionId || id;
    if (noteToDelete?.fromNotion && !notionId.startsWith('draft-')) {
      try {
        await deleteNotionNote(notionId);
      } catch (e) {
        console.error('Failed to delete from Notion:', e);
      }
    }
  };

  const filteredNotes = (searchQuery
    ? notes.filter(n =>
        (n.title || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (n.content || '').toLowerCase().includes(searchQuery.toLowerCase())
      )
    : notes
  ).slice().sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    if (date.toDateString() === now.toDateString()) {
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    }
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Desktop layout
  if (!isMobile) {
    return (
      <div className="flex h-full flex-1 overflow-hidden bg-white">
        {/* Sidebar */}
        <div
          className="notion-sidebar w-[400px]"
        >
          <div className="flex-shrink-0 px-3 pt-3 pb-2">
            <div className="flex items-center justify-between mb-2">
              <span className="px-1 text-[11px] font-semibold uppercase tracking-normal text-zinc-500">Notes</span>
              <Button type="button" plain onClick={createNote} className="h-8 w-8 rounded-full p-0" title="New note">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-400" />
              <Input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-white pl-8"
              />
              {searchQuery && (
                <Button type="button" plain onClick={() => setSearchQuery('')} className="absolute right-1.5 top-1/2 h-7 w-7 -translate-y-1/2 rounded-full p-0" aria-label="Clear search">
                  <X className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto px-1.5">
            {filteredNotes.length === 0 ? (
              <div className="flex h-full flex-col items-center justify-center px-4 text-zinc-400">
                <FileText className="mb-2 h-8 w-8 opacity-40" />
                <p className="text-xs">{searchQuery ? 'No matching notes' : 'No notes yet'}</p>
              </div>
            ) : (
              filteredNotes.map(n => (
                <button
                  key={n.id}
                  onClick={() => openNote(n.id)}
                  className={`notion-sidebar-item ${activeNoteId === n.id ? 'active' : ''}`}
                >
                  <span className="text-base flex-shrink-0 w-5 text-center">{n.emoji || '\u{1F4DD}'}</span>
                  <span className="flex-1 min-w-0 truncate text-left">{n.title || 'Untitled'}</span>
                  <span className="flex-shrink-0 tabular-nums text-[10px] text-zinc-400">{formatDate(n.updatedAt)}</span>
                </button>
              ))
            )}
          </div>
          <div className="flex-shrink-0 border-t border-zinc-200 px-3 py-2">
            <Button
              type="button"
              plain
              onClick={() => setShowPublished(true)}
              className="w-full justify-start px-2 py-1.5 text-xs"
            >
              <Globe className="h-3.5 w-3.5" />
              <span>Published</span>
              {publishedCount > 0 && (
                <Badge color="zinc" className="ml-auto px-1.5 py-0.5 text-[10px]">{publishedCount}</Badge>
              )}
            </Button>
          </div>
        </div>

        {/* Editor panel */}
        <div className="flex-1 flex flex-col overflow-hidden bg-white">
          {activeNote ? (
            <NoteEditor
              key={activeNote.id}
              note={activeNote}
              onSave={handleSave}
              onBack={() => { setActiveNoteId(null); onNoteNavigate?.(null); }}
              onDelete={confirmDelete}
              onPublishChange={handlePublishChange}
              isMobile={false}
            />
          ) : (
            <div className="flex flex-1 items-center justify-center text-zinc-400">
              <div className="text-center">
                <FileText className="mx-auto mb-3 h-12 w-12 opacity-20" />
                <p className="text-sm">Select a note or create a new one</p>
              </div>
            </div>
          )}
        </div>
        {deleteConfirmId && <DeleteConfirmModal noteTitle={notes.find(n => n.id === deleteConfirmId)?.title} onConfirm={() => handleDelete(deleteConfirmId)} onCancel={() => setDeleteConfirmId(null)} />}
        {showPublished && (
          <PublishedNotesModal
            notes={notes}
            onUnpublish={(noteId) => handlePublishChange(noteId, null)}
            onClose={() => setShowPublished(false)}
            onOpenNote={(id) => openNote(id)}
          />
        )}
      </div>
    );
  }

  // Mobile layout
  return (
    <div className="relative flex flex-1 flex-col overflow-hidden bg-zinc-100">
      <div className="flex-shrink-0 border-b border-zinc-200 bg-white px-4 py-3 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <h1 className="text-base font-semibold text-zinc-950">Notes</h1>
          <Button
            type="button"
            outline
            onClick={() => setShowPublished(true)}
            className="px-2.5 py-1.5 text-xs"
          >
            <Globe className="h-3.5 w-3.5" />
            <span>Published</span>
            {publishedCount > 0 && (
              <Badge color="zinc" className="px-1.5 py-0.5 text-[10px]">{publishedCount}</Badge>
            )}
          </Button>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
          <Input
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="bg-zinc-50 pl-9"
          />
          {searchQuery && (
            <Button type="button" plain onClick={() => setSearchQuery('')} className="absolute right-1.5 top-1/2 h-8 w-8 -translate-y-1/2 rounded-full p-0" aria-label="Clear search">
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {filteredNotes.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center px-4 text-zinc-400">
            <FileText className="mb-3 h-12 w-12 opacity-30" />
            <p className="text-sm">{searchQuery ? 'No matching notes' : 'No notes yet'}</p>
          </div>
        ) : (
          filteredNotes.map(note => (
            <button
              key={note.id}
              onClick={() => openNote(note.id)}
              className="notion-mobile-note-item"
            >
              <span className="text-2xl flex-shrink-0">{note.emoji || '\u{1F4DD}'}</span>
              <div className="flex-1 min-w-0 text-left">
                <div className="truncate text-sm font-medium text-zinc-950">{note.title || 'Untitled'}</div>
                <div className="mt-0.5 text-xs text-zinc-500">{formatDate(note.updatedAt)}</div>
              </div>
            </button>
          ))
        )}
      </div>

      {isMobile && (
        <>
          {!showSheet && (
            <Button
              onClick={createNote}
              className="absolute bottom-4 right-4 z-30 h-14 w-14 rounded-2xl p-0 active:scale-95"
            >
              <Plus className="h-6 w-6" />
            </Button>
          )}

          {showSheet && activeNote && (
            <>
              <div
                ref={backdropRef}
                className="fixed inset-0 z-40 bg-white"
                onClick={closeSheet}
              />
              <div
                ref={sheetRef}
                className="fixed inset-0 z-50 flex flex-col bg-white transition-transform duration-300 ease-out"
                style={{
                  transform: sheetAnimating ? 'translateX(0)' : 'translateX(100%)',
                  boxShadow: sheetAnimating ? '-12px 0 40px rgba(0, 0, 0, 0.15)' : 'none',
                  paddingBottom: 'max(12px, env(safe-area-inset-bottom))',
                  paddingTop: 'max(12px, env(safe-area-inset-top))'
                }}
                onTouchStart={handleSheetTouchStart}
                onTouchMove={handleSheetTouchMove}
                onTouchEnd={handleSheetTouchEnd}
              >
                <NoteEditor
                  key={activeNote.id}
                  note={activeNote}
                  onSave={handleSave}
                  onBack={closeSheet}
                  onDelete={confirmDelete}
                  onPublishChange={handlePublishChange}
                  isMobile
                />
              </div>
            </>
          )}
        </>
      )}
      {deleteConfirmId && <DeleteConfirmModal noteTitle={notes.find(n => n.id === deleteConfirmId)?.title} onConfirm={() => handleDelete(deleteConfirmId)} onCancel={() => setDeleteConfirmId(null)} />}
      {showPublished && (
        <PublishedNotesModal
          notes={notes}
          onUnpublish={(noteId) => handlePublishChange(noteId, null)}
          onClose={() => setShowPublished(false)}
          onOpenNote={(id) => openNote(id)}
          isMobile
        />
      )}
    </div>
  );
}

function PublishedNotesModal({ notes, onUnpublish, onClose, onOpenNote, isMobile }) {
  const publishedNotes = notes.filter(n => n.publishedSlug);
  const [unpublishing, setUnpublishing] = useState(null);

  const handleUnpublish = async (note) => {
    setUnpublishing(note.id);
    try {
      const notionId = note.notionId || note.id;
      await unpublishNote(notionId);
      onUnpublish(note.id);
    } catch (e) {
      console.error('Failed to unpublish:', e);
    } finally {
      setUnpublishing(null);
    }
  };

  const [copiedSlug, setCopiedSlug] = useState(null);
  const handleCopy = (slug) => {
    navigator.clipboard.writeText(`${window.location.origin}/p/${slug}`);
    setCopiedSlug(slug);
    setTimeout(() => setCopiedSlug(null), 2000);
  };

  return (
    <Dialog
      size="md"
      onClose={onClose}
      className={`overflow-hidden p-0 ${isMobile ? 'h-[calc(100dvh-1rem)]' : 'max-h-[70vh]'}`}
    >
      <div className="flex h-full flex-col">
        <div className="flex items-center justify-between border-b border-zinc-200 px-4 py-3">
          <div className="flex items-center gap-2">
            <Globe className="h-4 w-4 text-zinc-500" />
            <DialogTitle className="text-sm">Published Notes</DialogTitle>
          </div>
          <Button type="button" plain onClick={onClose} className="h-8 w-8 rounded-full p-0" aria-label="Close published notes">
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {publishedNotes.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-zinc-400">
              <Globe className="mb-2 h-8 w-8 opacity-30" />
              <p className="text-xs">No published notes</p>
            </div>
          ) : (
            publishedNotes.map(note => (
              <div key={note.id} className="group flex items-center gap-3 px-4 py-2.5 hover:bg-zinc-50">
                <button
                  type="button"
                  onClick={() => { onClose(); onOpenNote(note.id); }}
                  className="flex items-center gap-2 flex-1 min-w-0 text-left"
                >
                  <span className="text-base flex-shrink-0">{note.emoji || '\u{1F4DD}'}</span>
                  <div className="flex-1 min-w-0">
                    <div className="truncate text-sm font-medium text-zinc-950">{note.title || 'Untitled'}</div>
                    <div className="truncate text-[10px] text-zinc-500">/p/{note.publishedSlug}</div>
                  </div>
                </button>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <Button
                    type="button"
                    plain
                    onClick={() => handleCopy(note.publishedSlug)}
                    className={`h-8 w-8 rounded-full p-0 transition-all duration-200 ${copiedSlug === note.publishedSlug ? 'scale-110 bg-green-50 text-green-600' : 'text-zinc-500'}`}
                    title={copiedSlug === note.publishedSlug ? 'Copied!' : 'Copy link'}
                  >
                    {copiedSlug === note.publishedSlug ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                  </Button>
                  <Button
                    type="button"
                    plain
                    onClick={() => handleUnpublish(note)}
                    disabled={unpublishing === note.id}
                    className="h-8 w-8 rounded-full p-0 text-orange-500 hover:bg-orange-50"
                    title="Unpublish"
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </Dialog>
  );
}

function DeleteConfirmModal({ noteTitle, onConfirm, onCancel }) {
  return (
    <Dialog size="sm" onClose={onCancel}>
      <DialogTitle>Delete this page?</DialogTitle>
      <DialogDescription>
        "{noteTitle || 'Untitled'}" will be permanently deleted.
      </DialogDescription>
      <DialogActions>
        <Button type="button" outline onClick={onCancel}>Cancel</Button>
        <Button type="button" color="red" onClick={onConfirm}>Delete</Button>
      </DialogActions>
    </Dialog>
  );
}
